import OrderApiService from 'api/OrderApiService';
import { SearchParams } from 'api/ResourceAPI';
import { base64toBlob } from 'helpers/utils';
import useApi from 'hooks/useApi';
import useUserRoles from 'hooks/useUserRoles';
import { forEach, isEqual } from 'lodash';
import moment from 'moment';
import { useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import { closeDialogAction, openDialogAction } from 'store/actions/dialogsActions';
import { closeDrawerAction, openDrawerAction } from 'store/actions/drawerActions';
import { addLoadingAction, removeLoadingAction } from 'store/actions/loadingsActions';
import {
  changeCurrentTabAction,
  changePageAction,
  changeSearchParamsAction,
  deleteOrderAction,
  duplicateOrderAction,
  editManagementOrderAction,
  injectOrdersAction,
  setActiveOrderIdAction,
} from 'store/actions/managementActions';
import { getPurchaseOrderAction } from 'store/actions/purchaseOrderActions';
import { setSnackbarAction } from 'store/actions/snackbarActions';
import { useSelector } from 'store/hooks';
import { useAppState } from 'store/Provider';
import Catalog from 'types/entities/Catalog';
import Order from 'types/entities/Order';
import Scope from 'types/entities/Scope';
import { OrdersTabState } from 'types/enums/OrdersTabState';
import { OrderStatus } from 'types/enums/OrderStatus';
import useManagementHelper from './useManagementHelper';
import { appState } from 'store/states/appState';
import { changeCurrentTab } from 'features/Sheet/SheetContext/_reducers/CurrentTab/CurrentTab';
import { formatDateToddMMYYY } from 'helpers/format';
import { getbaseLineSourceOrderAction } from 'store/actions/changeBaselineActions';
import ChangeBaseLineAPIServices from 'api/ChangeBaseLineAPIServices';
import affairApiService from 'api/AffairApiService';
import { loadAffairsAction } from 'store/actions/appActions';

export default function useManagementService() {
  const { dispatch } = useAppState();
  const { app, management, filter, purchaseOrder } = useSelector((state) => state);
  // console.log();
  const userRoles = useUserRoles();
  const { makeCall } = useApi();
  const { isPathForProduction } = useManagementHelper();
  const history = useHistory();
  const appState = useSelector((state) => state.app);

  const fetchOrdersProduction = useCallback(
    async (searchParams: SearchParams) => {
      if (app.customer?.id) {
        return userRoles.isConsultant
          ? makeCall(OrderApiService.fetchCurrentOrders(app.customer.id, searchParams), '')
          : makeCall(OrderApiService.fetchCurrentOrdersListManagement(app.customer.id, searchParams), '');
      }
      return { orderForCustomer: null, totalPage: 0, currentPage: 0 };
    },
    [app.customer, makeCall, userRoles.isConsultant]
  );

  const fetchOrdersQuotes = useCallback(
    async (searchQuery: SearchParams) => {
      if (app.customer?.id) {
        return makeCall(OrderApiService.fetchCurrentCustomerOrdersList(app.customer.id, searchQuery), '');
      }
      return { orderForCustomer: null, totalPage: 0, currentPage: 0 };
    },
    [app.customer, makeCall]
  );

  const fetchOrders = useCallback(async () => {
    try {
      document.getElementById('management_page')?.scrollIntoView({ behavior: 'smooth' });
      dispatch(addLoadingAction('orders'));
      if (!management.searchParams || !management.searchParams.status) {
        return false;
      }

      dispatch(
        injectOrdersAction({
          orders: null,
          totalPages: 0,
          totalItems: 0,
        })
      );
      // history.replace({ state: '' });
      const { orderForCustomer, totalPage, currentPage } = isPathForProduction()
        ? await fetchOrdersProduction(management.searchParams)
        : await fetchOrdersQuotes(management.searchParams);
      // dispatch(changePageAction(currentPage + 1));
      const newPage = currentPage + 1;
      if (orderForCustomer !== null) {
        if (!orderForCustomer.length && totalPage && newPage >= totalPage) {
          dispatch(changePageAction(totalPage - 1));
        } else {
          dispatch(
            injectOrdersAction({
              orders: orderForCustomer,
              totalPages: totalPage,
              totalItems: 0,
              currentPage: newPage,
            })
          );
        }
      }
      dispatch(removeLoadingAction('orders'));
      return orderForCustomer;
    } catch (error) {
      dispatch(setSnackbarAction({ message: (error as Error)?.message, severity: 'error', open: true }));
      return false;
    }
  }, [dispatch, fetchOrdersProduction, fetchOrdersQuotes, isPathForProduction, management.searchParams]);

  const archiveOrder = async (orderId: Order['id']) => {
    await makeCall(OrderApiService.archive(orderId), 'Unable to archive order', `archive_${orderId}`);
    dispatch(deleteOrderAction(orderId));
  };
  const restoreOrder = async (orderId: Order['id']) => {
    await makeCall(OrderApiService.archive(orderId), 'Unable to restore order', `restore_${orderId}`);
    dispatch(deleteOrderAction(orderId));
  };

  const fetchMiniDashboardData = async (
    orderId: Order['id'],
    selectedScope: Scope['id'][],
    { from, to }: { from: string; to: string }
  ) =>
    makeCall(
      OrderApiService.getMiniDashboardData(orderId, selectedScope, from, to),
      'Unable to retrieve data for order',
      `miniDashboard${orderId}`
    );

  const getSortParams = useCallback(() => {
    if (management.pageHandler.currentTab === OrdersTabState.PENDING) {
      return 'pending_at.desc';
    }
    if (management.pageHandler.currentTab === OrdersTabState.DRAFTED) {
      return 'created_at.desc';
    }
    if (management.pageHandler.currentTab === OrdersTabState.TO_ASSIGN) {
      return 'validated_at.desc';
    }
    if (management.pageHandler.currentTab === OrdersTabState.ON_GOING) {
      return 'production_at.desc';
    }
    return 'created_at.desc';
  }, [management.pageHandler.currentTab]);
  const handleManagementSearchParams = useCallback(() => {
    const isEmpty =
      history.location.state !== null &&
      history.location.state !== '' &&
      history.location.state !== undefined &&
      !Number.isNaN(Number(history.location.state));
    const pageVal: number = history.location.state as number;
    const searchParams: SearchParams = {
      page: isEmpty ? pageVal : management.pageHandler.currentPage - 1,
      sorted: getSortParams(),
      size: 10,
      join: ['client', 'affair'],
    };
    if (filter.selected.text_search?.[0] && filter.selected.text_search?.[0] !== '')
      searchParams['reference,ref_spec,name_spec'] = `:${filter.selected.text_search?.[0]}:`;
    if (
      management.pageHandler.currentTab !== OrdersTabState.ARCHIVED &&
      management.pageHandler.currentTab !== OrdersTabState.MATERIAL_DELETED &&
      management.pageHandler.currentTab !== OrdersTabState.QUOTE
    ) {
      searchParams.is_archived = '0';
      searchParams.status = management.pageHandler.currentTab;
    } else if (management.pageHandler.currentTab === OrdersTabState.QUOTE) {
      searchParams.is_archived = '0';
      searchParams.status = `${OrdersTabState.DRAFTED},${OrdersTabState.PENDING},${OrdersTabState.TO_ASSIGN}`;
    } else if (management.pageHandler.currentTab === OrdersTabState.ARCHIVED) {
      searchParams.is_archived = '1';
      if (isPathForProduction()) {
        searchParams.status = `${OrdersTabState.ON_GOING},${OrdersTabState.TO_ASSIGN},${OrdersTabState.CLOSED},
         ${OrdersTabState.DRAFTED},${OrdersTabState.PENDING}`;
      } else {
        searchParams.status = `${OrdersTabState.DRAFTED},${OrdersTabState.PENDING}`;
      }
    } else if (management.pageHandler.currentTab === OrdersTabState.MATERIAL_DELETED) {
      searchParams.is_archived = '1';
      searchParams.status = `${OrdersTabState.MATERIAL_DRAFT},${OrdersTabState.MATERIAL_PRODUCTION},${OrdersTabState.MATERIAL_CLOSED}`;
    }
    if (filter.selected) {
      const ids: number[][] = [];
      forEach(filter.selected, (values, key) => {
        if (key === 'text_search') return;
        if (values.length && key.includes('date')) {
          if (key === 'date_start' && moment(values[0]).isValid()) {
            searchParams.end_date = `>=${values[0]}`;
            if (filter.selected.date_start.length > 0 && filter.selected.date_end.length === 0) {
              searchParams.start_date = `<=${values[0]}`;
            }
          }
          if (key === 'date_end' && moment(values[0]).isValid()) {
            searchParams.start_date = `<=${values[0]}`;
          }
        } else if (values.length) {
          const order_ids: string[] = [];
          values.forEach((v: string) => {
            const paramValues = filter.params?.[key];
            if (paramValues && paramValues.length) {
              const found = paramValues.find((property: any) => property.id === parseInt(v, 10));

              if (found) {
                if (found.order_id && Array.isArray(found.order_id)) {
                  // Old structure: property has an order_id array
                  found.order_id.forEach((f: string) => order_ids.push(f));
                } else {
                  // New structure: only id and name
                  order_ids.push(found.id); // or found.name if needed
                }
              }
            }
          });
          ids.push(order_ids.map((v) => parseInt(v, 10)));
        }
      });
      if (ids.length) {
        const isIdPresentInEveryOtherArrayIds = (id: number) => ids.every((arrayIds) => arrayIds.includes(id));
        const idsFound: number[] = [];
        ids.forEach((idsArray) => {
          idsArray.forEach((id) => {
            if (isIdPresentInEveryOtherArrayIds(id)) idsFound.push(id);
          });
        });
        if (idsFound.length) {
          searchParams.id = idsFound.join(',');
        } else {
          searchParams.id = -1;
        }
      }
    }
    if (isEqual(searchParams, management.searchParams)) return;
    dispatch(changeSearchParamsAction(searchParams));
  }, [
    management.pageHandler.currentPage,
    management.pageHandler.currentTab,
    management.searchParams,
    getSortParams,
    filter.selected,
    filter.params,
    dispatch,
    isPathForProduction,
  ]);

  const openManagementDuplicationDialog = (orderId: Order['id']) => {
    dispatch(setActiveOrderIdAction(orderId));
    dispatch(openDialogAction({ name: 'managementDuplicationDialog' }));
  };

  const openManagementCloseDialog = (orderId: Order['id']) => {
    dispatch(setActiveOrderIdAction(orderId));
    dispatch(openDialogAction({ name: 'managementCloseDialog' }));
  };

  const openManagementOpenDialog = (orderId: Order['id']) => {
    dispatch(setActiveOrderIdAction(orderId));
    dispatch(openDialogAction({ name: 'managementOpenDialog' }));
  };

  const fetchAffairs = async () => {
    const fetched = await affairApiService.fetchByCustomerId(appState.customer!.id);
    if (fetched?.length) dispatch(loadAffairsAction(fetched));
  };

  const validateOrder = async (order: Order) => {
    await fetchAffairs();
    if (!order?.affair_id || !order?.client_id) {
      dispatch(setActiveOrderIdAction(order.id));
      dispatch(openDrawerAction({ name: 'CompleteOrderDrawer' }));
      return undefined;
    }

    const {
      affair,
      client,
      discounts,
      price_discounted,
      'order-scopes': oscope,
      catalog,
      'order-workunits': owu,
      status,
      ...orderToSend
    } = order;
    const orderUpdated = await makeCall(
      OrderApiService.update(order.id, {
        ...orderToSend,
        id: order.id,
        discounts,
        // status: orderToSend.status === OrderStatus.PENDING ? OrderStatus.VALIDATED : orderToSend.status,
      }),
      'Error while updating order',
      'completeOrderLoading'
    );
    dispatch(editManagementOrderAction({ ...order, ...orderUpdated }));
    dispatch(closeDrawerAction());
    dispatch(setActiveOrderIdAction(null));
    // dispatch(deleteOrderAction(order.id));
    return orderUpdated;
  };

  const openChangeBaselineDialog = async (order: Order) => {
    dispatch(
      openDialogAction({
        name: 'changeBaselineOpenDialog',
        data: {
          order,
        },
      })
    );
  };

  const openChangeBaselineCancelDialog = async (order: Order) => {
    dispatch(
      openDialogAction({
        name: 'changeBaselineCancelDialog',
        data: { order },
      })
    );
  };

  const baselineCancelDialogApi = async (
    order: Order,
    page_type: number,
    baselineVersion: number,
    is_cancelled: number
  ) => {
    try {
      const data = await makeCall(
        ChangeBaseLineAPIServices.cancelBaseLineMissions({
          source_order_id: Number(order?.id),
          page_typeid: page_type,
          baseline_version: Number(baselineVersion),
          is_cancelled,
        })
      );

      if (data?.order) {
        fetchOrders();
        return data.order; // <--- ADD THIS LINE to return the order data on success
      }
      // If data?.order is falsy, you might want to return null or throw an error
      return null; // <--- Optional: Return null if order data is not found/successful
    } catch (error) {
      dispatch(
        setSnackbarAction({ message: 'The order baseline version has not been deleted', open: true, severity: 'error' })
      );
      // You might want to re-throw the error or return a specific error state
      throw error; // <--- Re-throw the error so the caller can handle it
    }
  };

  const baselineEngagementParaPage = async (order: Order) => {
    if (!order?.id) return;
    dispatch(getbaseLineSourceOrderAction(order));
    setTimeout(() => history.push(`/${app.customer?.slug}/changebaseline/order/${order?.id}`), 500);

    // dispatch(removeLoadingAction('purchaseorderlist'));
  };
  const duplicateOrder = async (orderId: Order['id'] | null) => {
    if (!orderId) return;
    const newOrder: Order = await makeCall(
      OrderApiService.duplicate(orderId),
      'Unable to duplicate order',
      `duplicate_${orderId}}`
    );
    if (
      management.pageHandler.currentTab === OrdersTabState.DRAFTED ||
      management.pageHandler.currentTab === OrdersTabState.QUOTE
    ) {
      const orderDuplicated = management.orders?.find((o) => o.id === orderId);
      if (!orderDuplicated) return;
      dispatch(duplicateOrderAction({ ...orderDuplicated, ...newOrder }));
      dispatch(setActiveOrderIdAction(newOrder.id));
    }

    if (
      newOrder.order_type_id === 2 &&
      (management.pageHandler.currentTab === OrdersTabState.MATERIAL_DRAFT ||
        management.pageHandler.currentTab === OrdersTabState.MATERIAL_PRODUCTION ||
        management.pageHandler.currentTab === OrdersTabState.MATERIAL_CLOSED ||
        management.pageHandler.currentTab === OrdersTabState.MATERIAL_DELETED)
    ) {
      const orderDuplicated = management.orders?.find((o) => o.id === orderId);
      if (!orderDuplicated) return;
      dispatch(duplicateOrderAction({ ...orderDuplicated, ...newOrder }));
      dispatch(setActiveOrderIdAction(newOrder.id));
      if (management.pageHandler.currentTab !== OrdersTabState.MATERIAL_DRAFT) {
        dispatch(changeCurrentTabAction(OrdersTabState.MATERIAL_DRAFT));
        history.push(`/${appState.customer?.slug}/orders/production?status=material_draft`);
      }
    }
    dispatch(closeDialogAction('managementDuplicationDialog'));
  };

  const downloadSelectedProductionExcel = async (orderId: Order['id']) => {
    try {
      const excel = await makeCall(OrderApiService.getOrderExcel(orderId));
      const outputFilename = `${excel.filename ? excel.filename : Date.now()}.xlsx`;

      const blob = base64toBlob(excel.data, 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet');
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', outputFilename);
      document.body.appendChild(link);
      link.click();
    } catch (error) {
      dispatch(
        setSnackbarAction({ message: 'Error while trying to create file to export', open: true, severity: 'error' })
      );
    }
  };

  const downloadBaselineVersionExcel = async (orderId: Order['id'], versionId: number) => {
    try {
      const excel = await makeCall(OrderApiService.getOrderBaselineExcel(orderId, versionId));
      const outputFilename = `${excel.filename ? excel.filename : `baseline_v${versionId}_${Date.now()}`}.xlsx`;

      const blob = base64toBlob(excel.data, 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet');
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', outputFilename);
      document.body.appendChild(link);
      link.click();

      // Clean up the URL object
      URL.revokeObjectURL(url);
      document.body.removeChild(link);
    } catch (error) {
      dispatch(
        setSnackbarAction({
          message: 'Error while trying to create baseline version file to export',
          open: true,
          severity: 'error',
        })
      );
    }
  };

  const navigateToSummary = (orderId: Order['id']) => {
    history.push(`/${app.customer?.slug}/orders/workunits/summary/${orderId}`);
  };
  const navigateToAffectation = (orderId: Order['id']) => {
    history.push(`/${app.customer?.slug}/orders/workunits/affectation/order/${orderId}`);
  };
  const navigateToOrderCreationWorkunits = (orderId: Order['id'], catalogId: Catalog['id']) => {
    history.push(`/${app.customer?.slug}/catalog/${catalogId}/order/${orderId}`);
  };
  const navigateToDeliverables = (orderId: Order['id']) => {
    history.push(`/${app.customer?.slug}/deliverables/${orderId}`);
  };

  const openEditOrderDrawer = (order: Order) => {
    dispatch(
      openDialogAction({
        name: 'EditOrderDrawer',
        data: { order, handleUpdateOrder: (order: Order) => editOrder(order) },
      })
    );
  };

  const editOrder = async (order: Order) => {
    const { status, 'order-workunits': removed, ...cleanOrderUpdate } = order;
    await makeCall(
      OrderApiService.update(order.id, cleanOrderUpdate),
      'Error while updating order',
      'updateOrderDrawerEdit'
    );
    dispatch(closeDrawerAction());
    dispatch(editManagementOrderAction({ ...order, ...cleanOrderUpdate }));
  };

  const openEditTimeMaterialDialog = async (order: Order, actionType: string) => {
    dispatch(addLoadingAction('purchaseorderlist'));
    dispatch(getPurchaseOrderAction([]));
    dispatch(
      openDialogAction({
        name: 'timeMaterialEditionOrder',
        data: {
          order,
          handleTMUpdateOrder: (order: Order, actionType: string) => editTimeMaterialOrder(order, actionType),
        },
      })
    );
    const purchaseOrderList = await makeCall(
      OrderApiService.fetchEditPurchaseOrder(order.id, order.affair?.otp as string)
    );
    if (purchaseOrderList.length > 0) {
      purchaseOrderList.forEach(
        (po: {
          poend: string;
          poref: string;
          reference: string;
          postart: string;
          is_valid: boolean;
          is_archived: boolean;
        }) => {
          po.reference = `${po.poref} (${formatDateToddMMYYY(po.postart)} - ${formatDateToddMMYYY(po.poend)})`;
          // po.is_archived = true;
        }
      );
    }
    dispatch(getPurchaseOrderAction(purchaseOrderList));
    dispatch(removeLoadingAction('purchaseorderlist'));
  };

  const editTimeMaterialOrder = async (order: Order, actionType: string) => {
    let updatedOrder = null;
    if (management.pageHandler.currentTab === OrdersTabState.MATERIAL_DRAFT && actionType === 'validate') {
      const { status, ...orderWithoutStatus } = order;
      updatedOrder = { ...orderWithoutStatus, status: OrderStatus.MATERIAL_PRODUCTION };
    } else {
      const { status, ...orderWithoutStatus } = order;
      updatedOrder = orderWithoutStatus;
    }
    const { 'order-workunits': removed, ...cleanOrderUpdate } = updatedOrder;
    await makeCall(
      OrderApiService.update(order.id, cleanOrderUpdate),
      'Error while updating order',
      'updateTimeMaterialOrderEdit'
    );
    dispatch(editManagementOrderAction({ ...order, ...cleanOrderUpdate }));
    //  console.log('management.pageHandler.currentTab: ', management.pageHandler.currentTab);
    //  console.log('actionType: ', actionType);
    if (management.pageHandler.currentTab === OrdersTabState.MATERIAL_DRAFT && actionType === 'validate') {
      dispatch(changeCurrentTabAction(OrdersTabState.MATERIAL_PRODUCTION));
      history.push(`/${appState.customer?.slug}/orders/production?status=material_production`);
    }
  };

  const closeOrder = async (orderId: Order['id'] | null, cancellation_reason: string | null = null) => {
    if (!orderId) {
      throw new Error('No order id provided');
    }
    const order = management?.orders?.find((o) => o.id === orderId);

    if (!order) {
      throw new Error(`Order ${orderId} not found`);
    }

    if (order.status !== OrderStatus.PRODUCTION && order.status !== OrderStatus.MATERIAL_PRODUCTION) {
      throw new Error(`Order ${orderId} is not in production`);
    }

    const response = await makeCall(
      OrderApiService.update(orderId, {
        status: order.status === OrderStatus.PRODUCTION ? OrderStatus.CLOSED : OrderStatus.MATERIAL_CLOSED,
        is_archived: false,
        cancellation_reason: cancellation_reason ?? undefined,
      }),
      'Error while updating order',
      `close_${orderId}`
    );

    if (response) {
      await fetchOrders();
    }
    return response;
  };

  const openOrder = async (orderId: Order['id'] | null) => {
    if (!orderId) {
      throw new Error('No order id provided');
    }
    const order = management?.orders?.find((o) => o.id === orderId);
    if (!order) {
      throw new Error(`Order ${orderId} not found`);
    }

    if (order.status !== OrderStatus.CLOSED && order.status !== OrderStatus.MATERIAL_CLOSED) {
      throw new Error(`Order ${orderId} is not closed`);
    }
    const response = await makeCall(
      OrderApiService.update(orderId, {
        status: order.status === OrderStatus.MATERIAL_CLOSED ? OrderStatus.MATERIAL_PRODUCTION : OrderStatus.PRODUCTION,
      }),
      'Error while updating order',
      `open_${orderId}`
    );

    if (response) {
      await fetchOrders();
    }
    return response;
  };

  const goBackStatus = useCallback(
    () => (isPathForProduction() ? OrdersTabState.ON_GOING : OrdersTabState.DRAFTED),
    [isPathForProduction]
  );

  const getOrderBaselineVersions = async (orderId: Order['id']) => {
    try {
      const response = await makeCall(
        OrderApiService.getOrderBaselineVersions(orderId),
        'Failed to fetch baseline versions'
      );
      return response;
    } catch (error) {
      console.error('Failed to check baseline versions:', error);
      throw error;
    }
  };

  return {
    fetchOrders,
    handleManagementSearchParams,
    archiveOrder,
    downloadSelectedProductionExcel,
    downloadBaselineVersionExcel,
    restoreOrder,
    navigateToSummary,
    navigateToAffectation,
    openChangeBaselineCancelDialog,
    openChangeBaselineDialog,
    baselineCancelDialogApi,
    baselineEngagementParaPage,
    navigateToDeliverables,
    duplicateOrder,
    openManagementDuplicationDialog,
    openManagementCloseDialog,
    openManagementOpenDialog,
    validateOrder,
    navigateToOrderCreationWorkunits,
    fetchMiniDashboardData,
    openEditOrderDrawer,
    closeOrder,
    openOrder,
    goBackStatus,
    openEditTimeMaterialDialog,
    getOrderBaselineVersions,
  };
}
